import {
  Grid,
  IconButton,
  Box,
  Collapse,
  Typography,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Card,
  CardContent,
} from "@mui/material";
import useQuestionListDetails from "../../Query/useQuestions";
import { useParams } from "react-router-dom";
import color from "../../Constants/colors";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Features, UserRole } from "../../Enums/companyStatus";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { hasRole, hasPermission } from "../../Helpers/authHelpers";
import { ComoInput } from "../Common/ComoInput";
import { ComoSwitch } from "../Common/ComoSwitch";

const SingleQuestionList = () => {
  const { t } = useTranslation("myQuestionLists");
  const { listId } = useParams<{ listId: string }>();
  const { data, isError, isLoading } = useQuestionListDetails(listId);
  const [expandedQuestions, setExpandedQuestions] = useState<
    Record<string, boolean>
  >({});
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({});
  const [expandAllAltTexts, setexpandAllAltTexts] = useState(false);
  const { colors: workspace } = useWorkspaceColors();

  const toggleAltText = (questionId: string) => {
    setExpandedQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const toggleAllAltTexts = () => {
    const newExpandAll = !expandAllAltTexts;
    setexpandAllAltTexts(newExpandAll);

    if (newExpandAll) {
      const allExpanded: Record<string, boolean> = {};
      data?.categories.forEach((category) => {
        category.questions.forEach((question) => {
          if (question.alt_text) {
            allExpanded[question.id] = true;
          }
        });
      });
      setExpandedQuestions(allExpanded);
    } else {
      setExpandedQuestions({});
    }
  };

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  if (isError) {
    return <div>Error: Failed to fetch Question List</div>;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: 3,
        overflowY: "auto",
        backgroundColor: color.gray50,
      }}
    >
      <Box
        sx={{
          maxWidth: "4xl",
          mx: "auto",
          gap: 3,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Card
          sx={{
            backgroundColor: color.gray50,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            mb: 2,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: workspace.primary_dark, mb: 0.5 }}
            >
              {t("myQuestionLists.questionListName")}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <ComoInput
                value={data?.name || ""}
                disabled
                fullWidth
                multiline
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: color.textPrimary,
                  },
                }}
              />

              {(hasRole(UserRole.ADMIN) ||
                hasPermission(Features.QA_INSIGHTS)) && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "0.8rem",
                      color: workspace.primary_dark,
                    }}
                  >
                    {expandAllAltTexts
                      ? t("myQuestionLists.toggleAltTextHide")
                      : t("myQuestionLists.toggleAltTextShow")}
                  </Typography>
                  <ComoSwitch
                    checked={expandAllAltTexts}
                    onChange={toggleAllAltTexts}
                  />
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Grid>
        {data?.categories.map((category: any) => (
          <Card
            key={category.id}
            sx={{
              backgroundColor: color.gray50,
              borderLeft: `4px solid ${workspace.primary_dark}`,
              mb: 2,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: workspace.primary_dark, mb: 0.5 }}
                  >
                    {t("myQuestionLists.questionCategory")}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <ComoInput
                      value={category.name}
                      disabled
                      fullWidth
                      multiline
                      sx={{
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: color.textPrimary,
                        },
                      }}
                    />

                    <Tooltip
                      title={
                        expandedCategories[category.id]
                          ? t("myQuestionLists.collapseCategory")
                          : t("myQuestionLists.expandCategory")
                      }
                    >
                      <IconButton
                        onClick={() => toggleCategory(category.id)}
                        sx={{ color: color.accent, ml: 1 }}
                      >
                        {expandedCategories[category.id] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>

              <Collapse in={expandedCategories[category.id] ?? false}>
                {(hasRole(UserRole.ADMIN) ||
                  hasPermission(Features.QA_INSIGHTS)) && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={category.extra_table}
                        disabled
                        sx={{
                          color: color.accent,
                          "&.Mui-checked": { color: color.accent },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body1"
                        sx={{
                          color: workspace.primary_dark,
                          fontSize: "0.9rem",
                        }}
                      >
                        {t("myQuestionLists.extraTable")}
                      </Typography>
                    }
                  />
                )}
                {(hasRole(UserRole.ADMIN) ||
                  hasPermission(Features.QA_INSIGHTS)) &&
                  category.extra_table &&
                  category.extra_table_properties && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Grid container spacing={1}>
                        {category.extra_table_properties.names.map(
                          (property: string, index: number) => (
                            <Grid item xs={12} sm={6} key={index}>
                              <Typography
                                variant="subtitle2"
                                sx={{ color: workspace.primary_dark, mb: 1 }}
                              >
                                {t("myQuestionLists.extraTableProperty")}{" "}
                                {index + 1}
                              </Typography>

                              <ComoInput
                                value={property || ""}
                                disabled
                                fullWidth
                                sx={{
                                  "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor: color.textPrimary,
                                  },
                                }}
                              />
                            </Grid>
                          ),
                        )}
                      </Grid>
                    </Box>
                  )}
                {(hasRole(UserRole.ADMIN) ||
                  hasPermission(Features.QA_INSIGHTS)) &&
                  category.subtitle && (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", mb: 2 }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ color: workspace.primary_dark, mb: 0.5 }}
                      >
                        {t("myQuestionLists.subtitle")}
                      </Typography>

                      <ComoInput
                        value={category.subtitle}
                        disabled
                        fullWidth
                        sx={{
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: color.textPrimary,
                          },
                        }}
                      />
                    </Box>
                  )}
                {(hasRole(UserRole.ADMIN) ||
                  hasPermission(Features.QA_INSIGHTS)) &&
                  category.description && (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", mb: 2 }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ color: workspace.primary_dark, mb: 0.5 }}
                      >
                        {t("myQuestionLists.description")}
                      </Typography>

                      <ComoInput
                        value={category.description}
                        disabled
                        fullWidth
                        multiline
                        rows={3}
                        sx={{
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: color.textPrimary,
                          },
                        }}
                      />
                    </Box>
                  )}
                <Grid
                  container
                  display={"flex"}
                  direction={"column"}
                  paddingLeft={2}
                  marginLeft={1}
                  sx={{ borderLeft: 1, borderColor: color.white }}
                >
                  {category.questions.map((question: any) => (
                    <Grid
                      container
                      direction="row"
                      spacing={1}
                      key={question.id}
                    >
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ color: workspace.primary_dark }}
                          >
                            {t("myQuestionLists.question")}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <ComoInput
                              value={question.question}
                              disabled
                              fullWidth
                              sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                  WebkitTextFillColor: color.textPrimary,
                                },
                              }}
                            />

                            {(hasRole(UserRole.ADMIN) ||
                              hasPermission(Features.QA_INSIGHTS)) && (
                              <Tooltip
                                title={
                                  expandedQuestions[question.id]
                                    ? t("myQuestionLists.collapseAltText")
                                    : t("myQuestionLists.expandAltText")
                                }
                              >
                                <span>
                                  <IconButton
                                    onClick={() => toggleAltText(question.id)}
                                    disabled={!question.alt_text}
                                    sx={{
                                      color: question.alt_text
                                        ? color.accent
                                        : color.secondary,
                                      ml: 1,
                                    }}
                                  >
                                    {expandedQuestions[question.id] ? (
                                      <ExpandLessIcon />
                                    ) : (
                                      <ExpandMoreIcon />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                          {(hasRole(UserRole.ADMIN) ||
                            hasPermission(Features.QA_INSIGHTS)) && (
                            <Collapse
                              in={expandedQuestions[question.id] || false}
                            >
                              <Box mt={1} ml={2}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    ml: 2,
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      color: workspace.primary_dark,
                                    }}
                                  >
                                    {t("myQuestionLists.altText")}
                                  </Typography>

                                  <ComoInput
                                    value={
                                      question.alt_text ||
                                      "No alt text available"
                                    }
                                    disabled
                                    fullWidth
                                    sx={{
                                      "& .MuiInputBase-input.Mui-disabled": {
                                        WebkitTextFillColor: color.textPrimary,
                                      },
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Collapse>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </CardContent>
          </Card>
        ))}
      </Grid>
    </Box>
  );
};

export default SingleQuestionList;
